import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import Spinner from 'components/Spinner';
import SelectFromList from 'components/SelectFromList';
import { sidebarStatePropType } from 'components/Sidebar/propTypes';
import { useIsMobileResolution } from 'utils/hooks/useIsMobileResolution';
import { OptionsListWithSearch } from './OptionsListWithSearch/OptionsListWithSearch';
import { QUESTION_VIEW_TYPES } from '../constants/questionViewTypes';
import QuestionTitle from './QuestionTitle';
import QuestionSubTitle from './QuestionSubTitle';
import QuestionOptionsContainer from './QuestionOptionsContainer';
import { FullScreenQuestionInput } from './FullScreenQuestionInput';

const { MULTISELECT_WITH_SEARCH, OPTIONS_LIST_WITH_SHOW_MORE } = QUESTION_VIEW_TYPES;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
  },
  selectFromList: {
    width: 450,
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
    },
  },
  optionsListWithSearch: {
    [theme.breakpoints.up('sm')]: {
      height: (props) => (props.isInsideSidebar ? null : 530),
    },
    [theme.breakpoints.down('xs')]: {
      height: (props) => (props.isInsideSidebar ? null : '50vh'),
    },
  },
}));

const inputComponentByViewType = {
  [MULTISELECT_WITH_SEARCH]: {
    component: SelectFromList,
    propsMap: ({
      id,
      searchPlaceholder,
      options,
      fixedOptions,
      className,
      isMultipleSelection,
      values,
      onChange,
      sidebarState: { isTransitionEntered } = {},
    }) => ({
      id,
      inputPlaceholder: searchPlaceholder,
      dropDownOptions: options,
      radioOptions: fixedOptions,
      rootClassName: className,
      isMultipleSelection,
      values,
      onChange,
      popupOpenTrigger: isTransitionEntered,
    }),
  },
  [OPTIONS_LIST_WITH_SHOW_MORE]: {
    component: OptionsListWithSearch,
    propsMap: (props) => props,
  },
};

export const MultipleOptionQuestionView = ({
  id,
  onChange,
  title,
  subtitle,
  dropDownOptions,
  radioOptions,
  groupedOptions,
  placeholder,
  values,
  sidebarState,
  viewType,
  questionProps = {},
  shouldNextButtonBeShown,
  handleNextButtonClick,
}) => {
  const classes = useStyles({ isInsideSidebar: !!sidebarState });
  const [isFullScreenQuestionInputMode, setIsFullScreenQuestionInputMode] = useState(false);

  const isMobile = useIsMobileResolution();

  const handleOptionsListSearchInputFocus = useCallback(() => {
    if (isMobile) {
      setIsFullScreenQuestionInputMode(true);
    }
  }, [isMobile]);

  const {
    component: InputComponent,
    propsMap: mapInputProps,
  } = inputComponentByViewType[viewType];

  const classesByViewType = {
    [MULTISELECT_WITH_SEARCH]: classes.selectFromList,
    [OPTIONS_LIST_WITH_SHOW_MORE]: classes.optionsListWithSearch,
  };

  const inputProps = {
    ...questionProps,
    isMultipleSelection: true,
    id,
    values,
    onChange,
    searchPlaceholder: placeholder,
    fixedOptions: radioOptions,
    options: dropDownOptions,
    groupedOptions,
    className: classesByViewType[viewType],
    sidebarState,
    onSearchInputFocus: handleOptionsListSearchInputFocus,
  };

  const renderQuestionInput = () => (
    dropDownOptions?.length ? (
      <InputComponent {...mapInputProps(inputProps)} />
    ) : (
      <Spinner />
    )
  );

  return (
    <>
      {isFullScreenQuestionInputMode && (
        <FullScreenQuestionInput
          shouldNextButtonBeShown={shouldNextButtonBeShown}
          handleNext={handleNextButtonClick}
          handleBack={() => setIsFullScreenQuestionInputMode(false)}
        >
          {renderQuestionInput()}
        </FullScreenQuestionInput>
      )}
      <div className={classes.root}>
        <QuestionTitle label={title} />

        {subtitle && <QuestionSubTitle label={subtitle} />}

        <QuestionOptionsContainer isFullWidth>
          {!isFullScreenQuestionInputMode && renderQuestionInput()}
        </QuestionOptionsContainer>
      </div>
    </>
  );
};

MultipleOptionQuestionView.propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  dropDownOptions: PropTypes.array.isRequired,
  radioOptions: PropTypes.array.isRequired,
  groupedOptions: PropTypes.array,
  placeholder: PropTypes.string,
  values: PropTypes.array.isRequired,
  sidebarState: sidebarStatePropType,
  viewType: PropTypes.oneOf([MULTISELECT_WITH_SEARCH, OPTIONS_LIST_WITH_SHOW_MORE]).isRequired,
  questionProps: PropTypes.object,
  shouldNextButtonBeShown: PropTypes.bool.isRequired,
  handleNextButtonClick: PropTypes.func.isRequired,
};
